import React, { useEffect, useState } from 'react'
import type { PageProps } from 'gatsby'
import { ThemeProvider } from 'styled-components'
import { AnimatePresence } from 'framer-motion'
import { Header } from 'components/Header/Header'
import { CovidBar } from 'components/CovidBar/CovidBar'
import { Footer } from '../Footer/Footer'
import { GlobalStyle } from 'utils/GlobalStyle'
import { theme } from 'utils/theme'
import { covidResponseLink } from 'data/links'
import { Nav } from '../Nav/Nav'
import { PromoBadge } from 'components/PromoBadge/PromoBadge'
import { SharedPageContext } from 'templates/types'
import {
  mapContentfulLinks,
  mapContentfulLinksGroups,
  mapContentfulLocations,
  mapContentfulStoreIconsToColorsSet,
} from 'utils/helpers'
import { analyticsUrlParamsIdentify } from 'utils/segment'

type LayoutProps = Omit<PageProps<unknown, SharedPageContext>, 'children'> & {
  children: React.ReactNode
  separatorVisible?: boolean
}

export const Layout = ({
  children,
  separatorVisible,
  pageContext,
}: LayoutProps) => {
  const {
    sharedPageData: {
      storesIcons,
      isPromoActive,
      promotion,
      legalLinks,
      linksGroups,
      covidBarText,
      locations,
    },
    logo,
    locationsData,
    prefixes: { locationPrefix },
  } = pageContext

  const storesIconsMapped = mapContentfulStoreIconsToColorsSet(storesIcons)

  const [isPromoBadgeVisible, setIsPromoBadgeVisible] = useState(true)

  analyticsUrlParamsIdentify()

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      <Header separatorVisible={separatorVisible}>
        <Nav storesIcons={storesIconsMapped.black} logo={logo} />
      </Header>
      <main>{children}</main>
      <AnimatePresence>
        {isPromoActive && promotion && isPromoBadgeVisible ? (
          <aside>
            <PromoBadge
              title={promotion.title}
              codeText={promotion.codeText}
              badge={promotion.badge.file.url}
              onCloseButtonClick={() => setIsPromoBadgeVisible(false)}
            />
          </aside>
        ) : null}
      </AnimatePresence>
      <Footer
        storesIcons={storesIconsMapped.white}
        legalLinks={mapContentfulLinks(legalLinks)}
        linksGroups={
          linksGroups ? mapContentfulLinksGroups(linksGroups) : undefined
        }
        locationsData={
          locationPrefix && locations
            ? {
                pathname: locationPrefix,
                locationsFooterSectionHeading: locations.footerSectionTitle,
                data: mapContentfulLocations(locationsData),
              }
            : undefined
        }
      />
      <CovidBar text={covidBarText} infoLink={covidResponseLink} />
    </ThemeProvider>
  )
}
